import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormField } from '@ipfabric/ui-components/FormField/FormField';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { displayTokenToLabelFilter } from '@ipfabric/utils';
import { isComputedReservedLabelName } from '@ipfabric/utils/labels';
import { useState } from 'react';
import { LabelPicker } from '../../shared/LabelPicker/LabelPicker';
import { ReservedLabelOverrideWarning } from '../../shared/ReservedLabelOverrideWarning';
import { OlderSnapshotAssignmentNotice } from '../OlderSnapshotAssignmentNotice';
import { ReflectToRulesSwitch } from '../ReflectToRulesSwitch';
import { updateAssignedLabel } from '../assignedLabelsApi';
import type { EditableAssignedLabel } from '../assignedLabelsTypes';
import { useIsLatestSnapshot } from '../useIsLatestSnapshot';

type EditAssignedLabelFormProps = {
  assignment: EditableAssignedLabel;
  onClose: () => void;
  onSaved: () => void;
  snapshotId: string;
};

const getTargetTypeLabel = (type: EditableAssignedLabel['type']) =>
  type === 'device' ? 'Device' : 'Interface';

export const EditAssignedLabelForm = ({
  assignment,
  onClose,
  onSaved,
  snapshotId,
}: EditAssignedLabelFormProps) => {
  const isLatestSnapshot = useIsLatestSnapshot(snapshotId);
  const [view, setView] = useState<'main' | 'labels'>('main');
  const [pickerDraftLabel, setPickerDraftLabel] = useState(assignment.label);
  const [selectedLabel, setSelectedLabel] = useState(assignment.label);
  const [reflectToRules, setReflectToRules] = useState(
    assignment.linkedToRule && isLatestSnapshot,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openLabelPicker = () => {
    setPickerDraftLabel(selectedLabel);
    setView('labels');
  };

  const applyPickerDraft = () => {
    if (pickerDraftLabel) {
      setSelectedLabel(pickerDraftLabel);
    }
    setView('main');
  };

  const handleSubmit = async () => {
    if (selectedLabel === assignment.label) {
      onClose();
      return;
    }

    setIsSubmitting(true);
    try {
      await updateAssignedLabel(snapshotId, assignment.labelId, {
        label: selectedLabel,
        reflectToRules,
      });
      onSaved();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SidePanel
      closeOnBackdropClick={false}
      footer={({ closeModal }) =>
        view === 'labels' ? (
          <div className='flex gap-xs'>
            <Button
              className='flex-1'
              dataTestId='edit-assigned-label-picker-apply'
              disabled={pickerDraftLabel.length === 0}
              onClick={applyPickerDraft}
              type='button'
              variant='primary'
            >
              Select label
            </Button>
            <Button
              className='flex-1'
              dataTestId='edit-assigned-label-picker-back'
              onClick={() => setView('main')}
              type='button'
              variant='outline'
            >
              Back
            </Button>
          </div>
        ) : (
          <div className='flex gap-xs'>
            <Button
              className='flex-1'
              dataTestId='edit-assigned-label-submit'
              disabled={selectedLabel.length === 0}
              loading={isSubmitting}
              onClick={() => void handleSubmit()}
              type='button'
              variant='primary'
            >
              Update label
            </Button>
            <Button className='flex-1' onClick={closeModal} variant='outline'>
              Close
            </Button>
          </div>
        )
      }
      onClose={onClose}
      title={view === 'labels' ? 'Label selection' : 'Edit assigned label'}
    >
      {view === 'main' ? (
        <Card className='flex flex-col gap p-sm'>
          {!isLatestSnapshot ? <OlderSnapshotAssignmentNotice /> : null}
          <FormField label='Target type'>
            <span className='text-sm'>
              {getTargetTypeLabel(assignment.type)}
            </span>
          </FormField>
          {assignment.hostname ? (
            <FormField label='Hostname'>
              <span className='text-sm'>{assignment.hostname}</span>
            </FormField>
          ) : null}
          <FormField label='Target'>
            <span className='text-sm'>{assignment.target}</span>
          </FormField>
          <FormField
            label='Label'
            link={
              <Button
                dataTestId='edit-assigned-label-change-label-link'
                icon='fa-pen'
                iconPosition='start'
                onClick={openLabelPicker}
                type='button'
                variant='plain'
              >
                Change label
              </Button>
            }
          >
            <span className='text-sm'>{selectedLabel}</span>
          </FormField>
          <ReservedLabelOverrideWarning
            names={[displayTokenToLabelFilter(selectedLabel).name].filter(
              isComputedReservedLabelName,
            )}
          />
          <ReflectToRulesSwitch
            checked={reflectToRules}
            onChange={setReflectToRules}
          />
        </Card>
      ) : (
        <LabelPicker
          onChange={(labels) => {
            setPickerDraftLabel(labels[0] ?? '');
          }}
          selectedLabels={pickerDraftLabel ? [pickerDraftLabel] : []}
          selectionMode='single'
        />
      )}
    </SidePanel>
  );
};
